import { useState } from "react";
import { useAuth, useLanguage } from "@/app";
import { createPayment } from "@/shared";
import { useUserStore } from "@/entities/user/model/store";
import { useBillingEmailGate } from "@/features/billing-email";
import { markSubscriptionCheckoutStarted, resolveAccountId, useSubscriptionStore, } from "@/features/subscription";
import { getPackagePriceRub } from "@/shared/api/packages";
import { navigateToPaymentPage, isWebPaymentEnv, } from "@/shared/lib/paymentNavigation";
import { rememberPendingPaymentForAnalytics } from "@/shared/lib/yandexMetrika";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
import { getApiErrorCode } from "@/shared/lib/apiError";
import { requestOpenAuthModal } from "@/shared/lib/authModalBridge";
import { getPaymentReturnUrl } from "@/shared/lib/paymentReturn";
import { SUBSCRIPTION_ERROR, getSubscriptionErrorMessage, isSubscriptionPackage, } from "@/shared/lib/subscriptionView";
import { toast } from "@/shared/ui/Toast/toast";
import { buildPaymentCreateRequest, decideSubscriptionResume, getSubscriptionPurchaseStep, resolvePackageProvider, } from "./purchaseLogic";
import { runSubscriptionResume } from "./subscriptionResume";
import { useSubscriptionConsent } from "./useSubscriptionConsent";
// Правила выбора провайдера живут в purchaseLogic; реэкспорт — для мест,
// которые исторически импортируют их отсюда.
export { canPayPackage, resolvePackageProvider } from "./purchaseLogic";
// Сколько ждём, пока вход доедет до стора пользователя (резюм после входа).
const RESUME_ACCOUNT_WAIT_MS = 3000;
const RESUME_ACCOUNT_POLL_MS = 150;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
/**
 * Единый флоу покупки пакета (тот же путь, что у `BuyPackageCard` на
 * `/balance`): billing-email гейт (54-ФЗ) → createPayment → аналитика →
 * навигация на кассу в жесте клика. Вынесен в хук, чтобы пейвол и обычный
 * выбор пакета шли ОДНИМ путём оплаты, а провайдеры/чек не дублировались.
 *
 * Подписочный пакет идёт тем же путём, но с двумя шагами перед ним: вход в
 * аккаунт (подписка привязана к аккаунту, гостю её не продаём) и модалка
 * согласия на автосписания. Разовая покупка этих шагов не видит.
 */
export function usePackagePurchase(options = {}) {
    const { user, isAuthenticated, isGuest, refreshUser, schedulePostEmailAuthAction, } = useAuth();
    const { language } = useLanguage();
    const { ensureBillingEmail, billingEmailModal } = useBillingEmailGate();
    const { ensureConsent, subscriptionConsentModal } = useSubscriptionConsent();
    const [purchasingId, setPurchasingId] = useState(null);
    const accountId = resolveAccountId({
        isAuthenticated: isAuthenticated && !isGuest,
        user,
    });
    const runPurchase = async (pkg, provider, payWindow, consentGiven = false) => {
        // Бэк требует billing_email для чека. Нет почты → спросим и сохраним,
        // иначе POST /payments вернёт 400.
        const gate = await ensureBillingEmail();
        if (!gate.ok) {
            payWindow?.close();
            return;
        }
        // Вкладка оплаты: открытая синхронно на клике (почта была) либо в жесте
        // сабмита модалки почты (почты не было). Обе null → фолбэк в текущей.
        const targetWindow = gate.payWindow ?? payWindow;
        setPurchasingId(pkg.id);
        try {
            const payment = await createPayment(buildPaymentCreateRequest({
                pkg,
                provider,
                billingEmail: gate.billingEmail,
                consentGiven,
                isWebEnv: isWebPaymentEnv(),
                returnUrl: getPaymentReturnUrl(),
            }));
            options.onPaymentCreated?.();
            // Создана ссылка/сессия оплаты (намерение; факт оплаты — в payments).
            trackEvent(AnalyticsEvent.PAYMENT_LINK_CREATED, {
                package_id: pkg.id,
                amount_rub: getPackagePriceRub(pkg),
                provider,
            });
            rememberPendingPaymentForAnalytics(payment.id);
            // Ушли оформлять подписку: когда пользователь вернётся (или придёт сигнал
            // оплаты), стор подписки нужно перечитать — см. useSubscriptionSync.
            if (isSubscriptionPackage(pkg))
                markSubscriptionCheckoutStarted();
            navigateToPaymentPage(payment.payment_url, targetWindow);
            // Обновляем баланс вскоре после открытия кассы.
            setTimeout(() => {
                void refreshUser();
            }, 2000);
        }
        catch (error) {
            targetWindow?.close();
            console.error("Ошибка создания платежа:", error);
            if (isSubscriptionPackage(pkg))
                reportSubscriptionError(error);
        }
        finally {
            setPurchasingId(null);
        }
    };
    const alreadySubscribedText = () => getSubscriptionErrorMessage(SUBSCRIPTION_ERROR.alreadyActive, language) ?? "";
    /** Отказ бэка в оформлении подписки — понятным текстом, а не тишиной. */
    const reportSubscriptionError = (error) => {
        const code = getApiErrorCode(error);
        toast.error(getSubscriptionErrorMessage(code, language) ??
            (language === "ru"
                ? "Не удалось оформить подписку. Попробуйте ещё раз."
                : "Couldn't start the subscription. Please try again."));
        if (code === SUBSCRIPTION_ERROR.alreadyActive && accountId !== null) {
            // Витрина не знала про подписку — перечитываем: пакет скроется, а на
            // /balance появится блок «Моя подписка».
            void useSubscriptionStore.getState().load(accountId, { force: true });
        }
        if (code === SUBSCRIPTION_ERROR.authRequired) {
            requestOpenAuthModal();
        }
    };
    /** Согласие на автосписания → оплата. Вызывать только для аккаунта. */
    const confirmAndPaySubscription = async (pkg, provider) => {
        const consent = await ensureConsent(pkg);
        if (!consent.ok)
            return;
        await runPurchase(pkg, provider, consent.payWindow, true);
    };
    /**
     * Продолжение после входа: гость нажал «Оформить», вошёл — показываем
     * согласие по тому же пакету, второй клик не нужен. Но сперва узнаём, нет ли
     * у вошедшего аккаунта подписки: предлагать согласие на вторую нельзя.
     *
     * Вызывает `SubscriptionResumeHost` (см. `subscriptionResume`), а не карточка:
     * карточки, по которой кликали, к этому моменту может уже не быть.
     */
    const resumeSubscriptionPurchase = async (pkg, provider) => {
        // Вход доезжает до стора пользователя не мгновенно — недолго ждём.
        const deadline = Date.now() + RESUME_ACCOUNT_WAIT_MS;
        let loggedInId = resolveAccountId(useUserStore.getState());
        while (loggedInId === null && Date.now() < deadline) {
            await sleep(RESUME_ACCOUNT_POLL_MS);
            loggedInId = resolveAccountId(useUserStore.getState());
        }
        if (loggedInId === null)
            return;
        // Ответ про подписку нужен именно по вошедшему аккаунту.
        await useSubscriptionStore.getState().load(loggedInId);
        const store = useSubscriptionStore.getState();
        const decision = decideSubscriptionResume({
            accountId: resolveAccountId(useUserStore.getState()),
            subscriptionOwnerId: store.userId,
            subscriptionResolved: store.resolved,
            hasSubscription: store.subscription !== null,
        });
        if (decision === "already_subscribed") {
            toast.info(alreadySubscribedText());
            return;
        }
        if (decision !== "consent")
            return;
        void confirmAndPaySubscription(pkg, provider);
    };
    const startSubscriptionPurchase = (pkg, provider) => {
        if (getSubscriptionPurchaseStep(accountId) === "auth") {
            // Штатный механизм «действие после входа» (как у создания песни):
            // вошёл — оформление продолжается само, второй клик не нужен.
            schedulePostEmailAuthAction(() => {
                runSubscriptionResume(pkg, provider);
            });
            if (!requestOpenAuthModal())
                schedulePostEmailAuthAction(null);
            return;
        }
        void confirmAndPaySubscription(pkg, provider);
    };
    /**
     * Старт покупки. Вызывать ПРЯМО в обработчике клика: window.open разрешён
     * только в пользовательском жесте, поэтому вкладку оплаты (веб + почта уже
     * есть) открываем синхронно ДО любого await. В мини-аппах вкладку не
     * открываем — оплата уходит во внешний браузер (openLink).
     */
    const startPurchase = (pkg) => {
        const provider = resolvePackageProvider(pkg);
        if (!provider)
            return;
        if (isSubscriptionPackage(pkg)) {
            // Вкладку оплаты здесь не открываем: впереди модалка согласия, и её клик
            // «Перейти к оплате» — тот самый жест для window.open.
            startSubscriptionPurchase(pkg, provider);
            return;
        }
        const hasBillingEmail = Boolean(user?.billing_email?.trim());
        const payWindow = isWebPaymentEnv() && hasBillingEmail
            ? window.open("about:blank", "_blank")
            : null;
        void runPurchase(pkg, provider, payWindow);
    };
    return {
        /** Запустить покупку пакета (в жесте клика). */
        startPurchase,
        /** id пакета, по которому сейчас создаётся платёж (для лоадера кнопки). */
        purchasingId,
        /** Идёт создание какого-либо платежа. */
        isPurchasing: purchasingId !== null,
        /** Модалка billing-email — отрендерить рядом с кнопкой оплаты. */
        billingEmailModal,
        /** Модалка согласия на автосписания — отрендерить там же (для подписок). */
        subscriptionConsentModal,
        /** Продолжить оформление подписки после входа (для SubscriptionResumeHost). */
        resumeSubscriptionPurchase,
    };
}
